<script lang="ts">
	import { userAccountStore } from '$lib/stores/auth-store';
	import { Portal, Dialog } from '@skeletonlabs/skeleton-svelte';
	import { Plug, CloseLarge, Power, DataRegular, Gamification } from 'carbon-icons-svelte';
	import { variables } from '$lib/env';
	import api from '$lib/services/kyAuthenticatedInstance';
	import { onMount } from 'svelte';
	import { goto, replaceState } from '$app/navigation';
	import { fetchEntitiesInArea } from '$lib/core/map/map-utils';
	import { page } from '$app/state';
	import { HTTPError } from 'ky';
	import { toast } from '../organisms/toast/toast';

	interface AvailableProfile {
		id: number;
		name: string;
		isUserDefaultProfile: boolean;
	}

	let selectedProfileId: number = $state($userAccountStore.profileId || 0);
	let loading = $state(true);
	let profiles = $state<AvailableProfile[] | null>(null);
	let isUpdating = $state(false);

	onMount(() => {
		api
			.get('Account/GetUserProfiles')
			.then(async (response) => {
				profiles = await response.json();
				loading = false;
			})
			.catch((error) => {
				console.error('Error during fetch:', error);
				loading = false;
			});
	});

	const loginTo = (profileId: number) => {
		isUpdating = true;
		api
			.put(`Account/SwitchProfile/${profileId}`)
			.then(async () => {
				if (page.route.id === '/map') {
					fetchEntitiesInArea({ sceneCenter: undefined, reloadArea: true });
				}
				replaceState('', {
					project: {
						id: undefined,
						name: undefined
					},
					plan: {
						id: undefined,
						name: undefined
					}
				});
				// Dirty quickfix
				window.location.reload();
			})
			.catch((error) => {
				if (error instanceof HTTPError) {
						toast({
							type: 'error',
							title: 'Bytte innlogget firma feilet',
							description: `${error.message}`
						});
					}
				console.error('Error during switch profile:', error);
				isUpdating = false;
			});
	};

    const updateDefaultUserProfile = (profileId: number) => {
        isUpdating = true;
        const shouldUpdate = profiles?.find(x => x.id === profileId);
        if (shouldUpdate !== undefined && !shouldUpdate.isUserDefaultProfile) {
            api.put(`Account/UpdateDefaultProfile/${profileId}`)
                .then(async (response) => {
                    isUpdating = false;
                })
                .catch((error) => {
                    console.error('Error during update default:', error);
                    isUpdating = false;
                });
        }
        else {
            isUpdating = false;
        }
    };
</script>
<div class="flex items-center justify-between">
    <h2 class="font-semibold text-lg mb-4">Bytt innlogget firma</h2>       
    <button
        type="button"
        disabled={isUpdating || loading}
        onclick={() => {
            updateDefaultUserProfile(selectedProfileId);
        }}
        class="btn btn-info mb-4"
        title={'Sett som standard'}
    >
        <Gamification size={20} class="mr-3" />
        Sett som standard
    </button>
</div>
{#if profiles}
	{@const getOptions = profiles
		.slice()
		.sort((a: AvailableProfile, b: AvailableProfile) => a.name.localeCompare(b.name))
		.map((profile: AvailableProfile) => ({
			label: profile.name,
			value: profile.id
		}))}
	<select
		disabled={isUpdating || loading}
		bind:value={selectedProfileId}
		class="select select-bordered select-primary w-full"
	>
		{#each getOptions as option}
			<option value={option.value}>{option.label}</option>
		{/each}
	</select>
{/if}

<div class="border-t pt-4 mt-4">
	<div class="flex row-spacing">
		<div class="basis-1/2">
			<button
				type="button"
				disabled={isUpdating || loading || $userAccountStore.profileId === selectedProfileId}
				onclick={() => {
					loginTo(selectedProfileId);
				}}
				class="btn btn-success w-full !rounded-full"
			>
				<Plug size={20} class="mr-3" />
				Bekreft bytte innlogget firma
			</button>
		</div>
		<div class="basis-1/2">
			<Dialog.CloseTrigger
				class="btn btn-border w-full !rounded-full"
				disabled={isUpdating || loading}
				><CloseLarge size={20} class="mr-3" />Avbryt</Dialog.CloseTrigger
			>
		</div>
	</div>
</div>
